import { commonService } from "../../../../utils/commonService";
import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  LineController,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import Loader from "../../../Loader/Loader";
import { Spinner } from "react-bootstrap";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  LineController,
);

const SERIES = [
  { key: "block", label: "Block", color: "#26A69A", type: "bar" },
  { key: "confirm", label: "Confirm", color: "#0B6195", type: "bar" },
  {
    key: "transient_otb",
    label: "Transient OTB",
    color: "#85B7EB",
    type: "bar",
  },
  { key: "d1", label: "1-Day", color: "#1D9E75", type: "line" },
  { key: "d3", label: "3-Day", color: "#EF9F27", type: "line" },
  { key: "d7", label: "7-Day", color: "#D85A30", type: "line" },
  {
    key: "wash",
    label: "System Wash",
    color: "#E24B4A",
    type: "line",
    isWash: true,
  },
  {
    key: "user_wash",
    label: "User Wash",
    color: "#8E24AA",
    type: "line",
    isWash: true,
  },
];

export default function GroupWashChart({
  groupWashData = [],
  oneDaypickupFromData = [],
  threeDayPickupFromData = [],
  sevenDayPickupFromData = [],
  isLoading = false,
}) {
  const [hidden, setHidden] = React.useState({});

  // 1. Safe parsing of x-axis date labels (MM-DD)
  const labels = useMemo(() => {
    return groupWashData.map((d) => (d?.date ? d.date.substring(5) : ""));
  }, [groupWashData]);

  // 2. Safe calculation of Days of the Week
  const dows = useMemo(() => {
    return groupWashData.map((d) => {
      if (!d?.date) return "";
      const dateObj = new Date(d.date + "T00:00:00");
      return isNaN(dateObj.getTime())
        ? ""
        : dateObj.toLocaleDateString("en-US", { weekday: "short" });
    });
  }, [groupWashData]);

  const gridColor = "rgba(0,0,0,.06)";
  const tickColor = "rgba(0,0,0,.55)";

  const toggleLegend = (key) => {
    setHidden((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // 3. Safe dataset extraction with fallback matching by date key
  const valuesFor = (key) => {
    switch (key) {
      case "block":
        return groupWashData.map((d) => d?.group_block ?? 0);
      case "confirm":
        return groupWashData.map((d) => d?.group_otb ?? 0);
      case "transient_otb":
        return groupWashData.map((d) => d?.transient_otb ?? 0);
      case "d1":
        return groupWashData.map((d) => {
          const match = oneDaypickupFromData.find(
            (p) => p?.dates === d?.date || p?.date === d?.date,
          );
          return match ? (match.rms ?? 0) : 0;
        });
      case "d3":
        return groupWashData.map((d) => {
          const match = threeDayPickupFromData.find(
            (p) => p?.dates === d?.date || p?.date === d?.date,
          );
          return match ? (match.rms ?? 0) : 0;
        });
      case "d7":
        return groupWashData.map((d) => {
          const match = sevenDayPickupFromData.find(
            (p) => p?.dates === d?.date || p?.date === d?.date,
          );
          return match ? (match.rms ?? 0) : 0;
        });
      case "wash":
        return groupWashData.map((d) => Number(d?.system_wash) || 0);
      case "user_wash":
        return groupWashData.map((d) => {
          const groupBlock = Number(d?.group_block) || 0;
          const userWashPct = parseFloat(d?.user_wash_pct) || 0;
          return Math.round((userWashPct * groupBlock) / 100);
        });
      default:
        return [];
    }
  };

  const datasets = useMemo(() => {
    const dsets = [];
    SERIES.forEach((s) => {
      if (hidden[s.key]) return;

      if (s.isWash) {
        dsets.push({
          type: "line",
          label: s.label,
          data: valuesFor(s.key),
          borderColor: s.color,
          backgroundColor: s.color,
          borderDash: [5, 4],
          tension: 0.3,
          pointRadius: 3,
          pointStyle: "rectRot",
          yAxisID: "y",
          stack: "wash",
          order: 1,
        });
      } else if (s.type === "line") {
        dsets.push({
          type: "line",
          label: s.label,
          data: valuesFor(s.key),
          borderColor: s.color,
          backgroundColor: s.color,
          borderWidth: 2.5,
          tension: 0.3,
          pointRadius: 2,
          yAxisID: "y",
          stack: s.key,
          order: 2,
        });
      } else {
        dsets.push({
          type: "bar",
          label: s.label,
          data: valuesFor(s.key),
          backgroundColor: s.color,
          yAxisID: "y",
          stack: s.key === "block" ? "block" : "otb",
          order: 3,
        });
      }
    });
    return dsets;
  }, [
    hidden,
    groupWashData,
    oneDaypickupFromData,
    threeDayPickupFromData,
    sevenDayPickupFromData,
  ]);

  const scales = {
    x: {
      grid: { display: false },
      ticks: {
        color: tickColor,
        autoSkip: false,
        maxRotation: 45,
        font: { size: 11 },
      },
      stacked: true,
    },
    y: {
      position: "left",
      grid: { color: gridColor },
      ticks: { color: tickColor },
      title: { display: true, text: "Rooms", color: tickColor },
      stacked: true,
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          afterTitle: (it) => dows[it[0].dataIndex] || "",
          label: (c) => {
            const isPct = false //c.dataset.label.includes("%") || c.dataset.label.toLowerCase().includes("wash");
            return c.dataset.label + ": " + c.parsed.y + (isPct ? "%" : " rms");
          },
        },
      },
    },
    scales,
  };

  // Gracefully render an empty dashboard state if master data isn't present
  if (!groupWashData || groupWashData.length === 0) {
    return (
      <div className="groupwash-empty-container">
        {isLoading ? (
          <div className="groupwash-spinner-container">
            <div style={{ height: "80px", position: "relative" }}>
              <Loader />
            </div>
          </div>
        ) : (
          "No chart data available."
        )}
      </div>
    );
  }

  return (
    <div className={`groupwash-chart-card ${isLoading ? "loading" : ""}`}>
      {isLoading && (
        <div className="groupwash-overlay">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading chart...</span>
          </Spinner>
        </div>
      )}
      <div>
        <header className="groupwash-header">
          <h6 className="groupwash-title">
            Block · Confirm · Transient OTB · Snapshots · System Wash
          </h6>
          <p className="groupwash-subtitle">
            Unified dashboard tracking allocations, vertical live picks,
            snapshots and system wash.
          </p>
        </header>

        <div className={`groupwash-body-card ${isLoading ? "loading" : ""}`}>
          <div className="groupwash-legend-container">
            {SERIES.map((s) => {
              const isHidden = hidden[s.key];
              return (
                <span id={commonService.generateGlobalClickTrackerID("GroupWashChart", "span-onclick", s)}
                  key={s.key}
                  onClick={() => toggleLegend(s.key)}
                  className={`groupwash-legend-item ${isHidden ? "hidden" : ""}`}
                >
                  {s.isWash ? (
                    <span
                      className="groupwash-legend-wash-line"
                      style={{ borderTopColor: s.color }}
                    ></span>
                  ) : s.type === "line" ? (
                    <span
                      className="groupwash-legend-solid-line"
                      style={{ borderTopColor: s.color }}
                    ></span>
                  ) : (
                    <span
                      className="groupwash-legend-color-box"
                      style={{ background: s.color }}
                    ></span>
                  )}
                  {s.label}
                </span>
              );
            })}
          </div>
          <div className="groupwash-chart-wrapper">
            <Chart type="bar" data={{ labels, datasets }} options={options} />
          </div>
        </div>
      </div>
    </div>
  );
}
